import { Autocomplete, Card, Checkbox, Divider, FormControlLabel, Skeleton, Stack, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useContext, useEffect, useState } from "react";
import { clienteFormularioType, NewFormContext } from "../../context/NewFormContext";
import AttToken from "../../helpers/attToken";
import Checklists from "../../service/Checklists";
import Clients from "../../service/Clients";
import { ClientType } from "../../types/clientes/types";
import { useTranslation } from "react-i18next";

const customerService = new Clients();
const checklistService = new Checklists();

export default function ConfigCLiente() {
  const { modelo, cliente, setCliente, clienteFormulario, setClienteFormulario } = useContext(NewFormContext);
  const { t } = useTranslation("translation");
  const [distanciaLimiteInput, setDistanciaLimiteInput] = useState('');

  useEffect(() => {
    setCliente(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["GET_CLIENTES"],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error(t('Reports.reportsTable.tokenNotFound'));
        const response = await customerService.getAllClients(token, false);
        if (!response?.clientList) throw new Error(t('checklists.configClienteForm.clienteSemPermissao'));
        return response.clientList;
      } catch (error) {
        throw new Error(t('checklists.configClienteForm.erroBuscarClientes'));
      }
    },
    retry: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });

  const {
    data: clienteFormularioData,
    isLoading: clienteFormularioLoading,
    isFetching: clienteFormularioFetching,
    isSuccess,
  } = useQuery({
    queryKey: ["GET_CLIENTE_FORMULARIO", cliente],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error(t('Reports.reportsTable.tokenNotFound'));
        if (token && cliente && modelo) {
          const response = await checklistService.getFormConfigData(token, modelo?.id, cliente.id as string);
          if (!response) throw new Error(t('checklists.configClienteForm.erroBuscarConfiguracoes'));
          return response;
        }
      } catch (error) {
        throw new Error(t('checklists.configClienteForm.erroBuscarConfiguracoes'));
      }
    },
    enabled: cliente !== null,
    retry: 2,
    refetchOnWindowFocus: false,
    refetchOnMount: "always",
  });

  const onLoading = isLoading || isFetching;

  const onLoadingClienteFormulario = clienteFormularioLoading || clienteFormularioFetching;

  useEffect(() => {
    if (isSuccess && cliente) {
      setClienteFormulario({
        limiteChamado: 0,
        tipoChamado: [],
        chamadoObrigatorio: false,
        duracaoValidade: 0,
        id: `${cliente?.id}_${modelo?.id}`,
        formulario: modelo?.id,
        cliente: cliente?.id,
        gravaPOS: clienteFormularioData?.gravaPOS ?? false,
        delegacao: clienteFormularioData?.delegacao ?? false,
        qrcode: clienteFormularioData?.qrcode ?? false,
        distanciaLimite: clienteFormularioData?.distanciaLimite ?? 0,
      });
      setDistanciaLimiteInput(String(clienteFormularioData?.distanciaLimite ?? 0).replace('.', ','));
    }
  }, [isSuccess, cliente, clienteFormularioData, onLoading, modelo?.id, setClienteFormulario]);

  function handleChangeGravaPOS(event: React.ChangeEvent<HTMLInputElement>) {
    setClienteFormulario((prev: clienteFormularioType) => prev ? { ...prev, gravaPOS: event.target.checked } : false);
  }

  useEffect(() => {
    if (!clienteFormulario?.gravaPOS) {
      setClienteFormulario({
        ...clienteFormulario,
        distanciaLimite: 0,
      });
      setDistanciaLimiteInput('0');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteFormulario?.gravaPOS]);

  function handleChangeQrCode(event: React.ChangeEvent<HTMLInputElement>) {
    setClienteFormulario((prev: clienteFormularioType) => prev ? { ...prev, qrcode: event.target.checked } : null);
  }

  function handleChangeDelegacao(event: React.ChangeEvent<HTMLInputElement>) {
    setClienteFormulario((prev: clienteFormularioType) => prev ? { ...prev, delegacao: event.target.checked } : null);
  }

  function handleChangeDistanciaLimite(event: React.ChangeEvent<HTMLInputElement>) {
    const inputValue = event.target.value;
    const normalizedValue = inputValue.replace(',', '.');

    // Mantém o texto em edição (por exemplo, "1," ou "1.") até o usuário concluir o número.
    if (!/^\d*\.?\d*$/.test(normalizedValue)) return;

    setDistanciaLimiteInput(inputValue);
    const numericValue = normalizedValue === '' || normalizedValue === '.' ? 0 : Number(normalizedValue);
    setClienteFormulario((previous: clienteFormularioType | null) => previous
      ? { ...previous, distanciaLimite: Number.isFinite(numericValue) ? numericValue : 0 }
      : previous
    );
  }

  const Loading = () => {
    return (
      <>
        <Typography color="textDisabled" fontSize="medium" fontWeight={600}>
          {t('checklists.configClienteForm.configLocalizacao')}
        </Typography>
        <Skeleton variant="text" width={250} />
        <Stack direction="row" spacing={1} alignItems="center">
          <Skeleton variant="rounded" height={50} width={250} />
          <Typography color="textDisabled" fontSize="medium">
            {t('checklists.configClienteForm.unidadeDistancia')}
          </Typography>
        </Stack>
        <Divider orientation="horizontal" flexItem variant="middle" />
        <Typography color="textDisabled" fontSize="medium" fontWeight={600}>
          {t('checklists.configClienteForm.configFormulario')}
        </Typography>
        <Skeleton variant="text" width={250} />
        <Skeleton variant="text" width={250} />
      </>
    );
  };

  return (
    <Card sx={{ width: "100%", padding: 2 }}>
      <Stack width="100%" direction="column" justifyContent="center" alignItems="flex-start" spacing={2}>
        <Typography color="textDisabled" variant="h5" textAlign="center" fontWeight={600} width="100%">
          {t('checklists.configClienteForm.titulo')}
        </Typography>
        <Autocomplete
          fullWidth
          disabled={onLoading}
          loading={onLoading}
          loadingText={t('checklists.configClienteForm.carregandoDados')}
          autoComplete
          value={cliente ?? null}
          getOptionLabel={(option) => option.nome as string}
          getOptionKey={(option) => option?.id as string}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterSelectedOptions
          options={
            data
              ?.sort((a: ClientType, b: ClientType) => {
                return a.nome?.toLowerCase() > b.nome?.toLowerCase() ? 1 : -1;
              })
              ?.map((cliente: ClientType) => ({ ...cliente })) ?? []
          }
          onChange={(event: any, newValue: ClientType | null) => {
            setCliente(newValue);
          }}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              <span>{option.nome}</span>
            </li>
          )}
          renderInput={(params) => <TextField {...params} variant="outlined" label={t('checklists.configClienteForm.selecioneCliente')} />}
        />
        {onLoadingClienteFormulario ? (
          <Loading />
        ) : (
          cliente && (
            <>
              <Typography color="textDisabled" fontSize="medium" fontWeight={600}>
                {t('checklists.configClienteForm.configLocalizacao')}
              </Typography>
              <FormControlLabel
                control={<Checkbox onChange={handleChangeGravaPOS} checked={clienteFormulario?.gravaPOS} />}
                label={t('checklists.configClienteForm.habilitarControleCerca')}
              />
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  disabled={!clienteFormulario?.gravaPOS}
                  type="text"
                  slotProps={{ htmlInput: { style: { textAlign: "center" }, inputMode: 'decimal', pattern: '[0-9]*[.,]?[0-9]*' } }}
                  onChange={handleChangeDistanciaLimite}
                  value={distanciaLimiteInput}
                  label={t('checklists.configClienteForm.limiteAte')}
                />
                <Typography color="textDisabled" fontSize="medium">
                  {t('checklists.configClienteForm.unidadeDistancia')}
                </Typography>
              </Stack>
              <Divider orientation="horizontal" flexItem variant="middle" />
              <Typography color="textDisabled" fontSize="medium" fontWeight={600}>
                {t('checklists.configClienteForm.configFormulario')}
              </Typography>
              <FormControlLabel control={<Checkbox onChange={handleChangeQrCode} checked={clienteFormulario?.qrcode} />} label={t('checklists.configClienteForm.habilitarQrCode')} />
              <FormControlLabel
                control={<Checkbox onChange={handleChangeDelegacao} checked={clienteFormulario?.delegacao} />}
                label={t('checklists.configClienteForm.permitirDelegacao')}
              />
            </>
          )
        )}
      </Stack>
    </Card>
  );
}
